import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { NAVIGATION_ITEMS } from '../data/constants.jsx'
import { getIcon, useIcon } from '../hooks/useIcons.jsx'
import Button from './ui/Button'
import LanguageSwitcher from './LanguageSwitcher'
import DonateButton from './DonateButton'
import Logo from './ui/Logo'

const Navbar = ({ user, onLogout }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const MenuIcon = useIcon('Menu')

  return (
    <nav className="bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <Logo variant="lockupOnLight" className="h-10 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {NAVIGATION_ITEMS.map((item) => {
              const IconComponent = useIcon(item.icon)
              const isActive = location.pathname === item.path
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-smooth ${
                    isActive
                      ? 'bg-accent text-accent-foreground'
                      : 'hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <IconComponent className="w-4 h-4" />
                  <span className="font-medium">{t(`nav.${item.key}`)}</span>
                </Link>
              )
            })}
            
            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* Donate button */}
            <DonateButton />

            {/* Auth buttons */}
            <div className="ml-2 flex items-center space-x-2">
              {user ? (
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-muted-foreground">
                    {user.name}
                  </span>
                  <Button
                    onClick={onLogout}
                    variant="outline"
                    className="text-sm"
                  >
                    {t('nav.logout')}
                  </Button>
                </div>
              ) : (
                <>
                  <Button
                    onClick={() => navigate('/login')}
                    variant="outline"
                    className="text-sm"
                  >
                    {t('auth.login')}
                  </Button>
                  <Button
                    onClick={() => navigate('/register')}
                    className="text-sm"
                  >
                    {t('auth.register')}
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden inline-flex items-center justify-center p-2 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            <MenuIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Mobile Navigation */}
        <div className={`md:hidden transition-all duration-300 ease-in-out ${
          isMenuOpen ? 'max-h-screen overflow-y-auto' : 'max-h-0 overflow-hidden'
        }`}>
          <div className="pt-4 pb-4 space-y-2">
            {NAVIGATION_ITEMS.map((item) => {
              const IconComponent = useIcon(item.icon)
              const isActive = location.pathname === item.path
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`w-full flex items-center space-x-2 px-4 py-3 rounded-lg transition-smooth justify-start ${
                    isActive
                      ? 'bg-accent text-accent-foreground'
                      : 'hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <IconComponent className="w-4 h-4" />
                  <span className="font-medium">{t(`nav.${item.key}`)}</span>
                </Link>
              )
            })}
            
            {/* Mobile Language Switcher */}
            <div className="pt-4 border-t border-border">
              <LanguageSwitcher />
            </div>

            {/* Mobile Donate button */}
            <div className="pt-2">
              <DonateButton className="w-full justify-center" />
            </div>

            {/* Mobile Auth buttons */}
            <div className="pt-4 border-t border-border space-y-2">
              {user ? (
                <>
                  <div className="px-4 py-2 text-sm text-muted-foreground">
                    {t('nav.loggedInAs')}: {user.name}
                  </div>
                  <Button
                    onClick={() => {
                      onLogout()
                      setIsMenuOpen(false)
                    }}
                    variant="outline"
                    className="w-full"
                  >
                    {t('nav.logout')}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={() => {
                      navigate('/login')
                      setIsMenuOpen(false)
                    }}
                    variant="outline"
                    className="w-full"
                  >
                    {t('auth.login')}
                  </Button>
                  <Button
                    onClick={() => {
                      navigate('/register')
                      setIsMenuOpen(false)
                    }}
                    className="w-full"
                  >
                    {t('auth.register')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  )
}

export default Navbar